import { useMemo, useCallback } from 'react';
import type { TableWidgetConfig, ExecutionDataRow, AggregationType, MetricDefinition } from '@monitoring-panel/types';
import { formatNumber, formatDate } from '@/lib/utils';
import { evaluateFormula, getFormulaDependencies } from '@/lib/formula';

interface TableWidgetProps {
  config: TableWidgetConfig;
  campaignData: ExecutionDataRow[];
  availableMetrics: MetricDefinition[];
}

export function TableWidget({ config, campaignData, availableMetrics }: TableWidgetProps) {
  const { columns, aggregation, showTotals, groupBy, dimensions: configDimensions } = config;

  // Determine effective dimensions: prefer new dimensions field, fall back to groupBy
  const dimensions = useMemo(() => {
    if (configDimensions !== undefined) {
      return configDimensions;
    }
    // Backward compatibility: convert groupBy to dimensions
    if (groupBy === 'date') {
      return ['date'];
    }
    // groupBy === 'none' or undefined
    return [];
  }, [configDimensions, groupBy]);

  // Build a map of metric definitions by ID for quick lookup
  const metricsById = useMemo(() => {
    const map = new Map<string, MetricDefinition>();
    for (const m of availableMetrics) {
      map.set(m.id, m);
    }
    return map;
  }, [availableMetrics]);

  // Helper to get display name for a column
  const getColumnDisplayName = useCallback((col: string): string => {
    if (col.startsWith('metric:')) {
      const metricId = col.substring(7);
      const metric = metricsById.get(metricId);
      return metric?.name || metricId;
    }
    return formatColumnName(col);
  }, [metricsById]);

  // Helper to get format for a column
  const getColumnFormat = useCallback((col: string): string => {
    if (col.startsWith('metric:')) {
      const metricId = col.substring(7);
      const metric = metricsById.get(metricId);
      return metric?.format || 'number';
    }
    return 'auto';
  }, [metricsById]);

  const processedData = useMemo(() => {
    if (campaignData.length === 0) return { rows: [], totals: {}, dimensions };

    // Separate raw columns from calculated metrics
    const rawColumns: string[] = [];
    const metricColumns: string[] = [];
    for (const col of columns) {
      if (col.startsWith('metric:')) {
        metricColumns.push(col);
      } else {
        rawColumns.push(col);
      }
    }

    // Collect all raw column dependencies needed for calculated metrics
    const allDependencies = new Set<string>(rawColumns);
    for (const col of metricColumns) {
      const metricId = col.substring(7);
      const metric = metricsById.get(metricId);
      if (metric) {
        const deps = getFormulaDependencies(metric.formula);
        for (const dep of deps) {
          allDependencies.add(dep);
        }
      }
    }

    // Helper to calculate metrics from aggregated raw values
    const calculateMetricsFromAggregated = (aggregatedValues: Record<string, number>): Record<string, number> => {
      const result: Record<string, number> = { ...aggregatedValues };
      for (const col of metricColumns) {
        const metricId = col.substring(7);
        const metric = metricsById.get(metricId);
        if (metric) {
          const formulaResult = evaluateFormula(metric.formula, aggregatedValues);
          result[col] = formulaResult ?? 0;
        }
      }
      return result;
    };

    // Filter dimensions to only include those that exist in the data
    const validDimensions = dimensions.filter((dim: string) => {
      if (campaignData.length === 0) return true;
      return dim in campaignData[0];
    });

    if (validDimensions.length > 0) {
      // Group by multiple dimensions using composite key
      const grouped = new Map<string, { dimensionValues: Record<string, unknown>; values: Record<string, number[]> }>();

      for (const row of campaignData) {
        // Create composite key from dimension values
        const dimensionValues: Record<string, unknown> = {};
        const keyParts: string[] = [];
        for (const dim of validDimensions) {
          const value = row[dim];
          dimensionValues[dim] = value;
          keyParts.push(String(value ?? ''));
        }
        const groupKey = keyParts.join('|||');

        if (!grouped.has(groupKey)) {
          grouped.set(groupKey, { dimensionValues, values: {} });
        }
        const group = grouped.get(groupKey)!;

        // Collect all raw dependencies
        for (const col of allDependencies) {
          const value = row[col];
          if (typeof value === 'number') {
            if (!group.values[col]) group.values[col] = [];
            group.values[col].push(value);
          }
        }
      }

      const rows = Array.from(grouped.values()).map(({ dimensionValues, values }) => {
        // First aggregate raw dependencies
        const aggregatedRaw: Record<string, number> = {};
        for (const col of allDependencies) {
          aggregatedRaw[col] = aggregate(values[col] || [], aggregation);
        }

        // Then calculate metrics from aggregated values
        const withMetrics = calculateMetricsFromAggregated(aggregatedRaw);

        return { ...dimensionValues, ...withMetrics } as Record<string, unknown>;
      });

      // Sort by dimensions in order (primary, secondary, etc.)
      rows.sort((a, b) => {
        for (const dim of validDimensions) {
          const aVal = String(a[dim] ?? '');
          const bVal = String(b[dim] ?? '');
          const cmp = aVal.localeCompare(bVal);
          if (cmp !== 0) return cmp;
        }
        return 0;
      });

      // Calculate totals - aggregate all raw dependencies first, then calculate metrics
      const allRawValues: Record<string, number[]> = {};
      for (const row of campaignData) {
        for (const col of allDependencies) {
          const value = row[col];
          if (typeof value === 'number') {
            if (!allRawValues[col]) allRawValues[col] = [];
            allRawValues[col].push(value);
          }
        }
      }

      const totalAggregatedRaw: Record<string, number> = {};
      for (const col of allDependencies) {
        totalAggregatedRaw[col] = aggregate(allRawValues[col] || [], 'sum');
      }

      const totals = calculateMetricsFromAggregated(totalAggregatedRaw);

      return { rows, totals, dimensions: validDimensions };
    } else {
      // No dimensions - aggregate all raw data into a single row
      const values: Record<string, number[]> = {};

      for (const row of campaignData) {
        for (const col of allDependencies) {
          const value = row[col];
          if (typeof value === 'number') {
            if (!values[col]) values[col] = [];
            values[col].push(value);
          }
        }
      }

      const aggregatedRaw: Record<string, number> = {};
      for (const col of allDependencies) {
        aggregatedRaw[col] = aggregate(values[col] || [], aggregation);
      }

      // Calculate metrics from aggregated values
      const aggregated = calculateMetricsFromAggregated(aggregatedRaw);

      return { rows: [aggregated], totals: aggregated, dimensions: [] };
    }
  }, [campaignData, columns, aggregation, dimensions, metricsById]);

  const formatValue = useCallback((value: number | undefined, col: string): string => {
    if (value === undefined || value === null) return '-';

    const format = getColumnFormat(col);
    if (format !== 'auto') {
      return formatNumber(value, format as 'percentage' | 'euro' | 'dollar' | 'number' | 'decimal');
    }

    // Auto-detect format based on column name
    const lowerCol = col.toLowerCase();
    if (lowerCol.includes('cost') || lowerCol.includes('spent') || lowerCol.includes('revenue')) {
      return formatNumber(value, 'euro');
    }
    if (lowerCol.includes('rate') || lowerCol.includes('percentage') || lowerCol.includes('ctr')) {
      return formatNumber(value, 'percentage');
    }
    return formatNumber(value, 'number');
  }, [getColumnFormat]);

  // Helper to format dimension values appropriately
  const formatDimensionValue = useCallback((value: unknown, dim: string): string => {
    if (value === undefined || value === null) return '-';
    // Format date dimensions
    if (dim === 'date' || dim.toLowerCase().endsWith('date')) {
      return formatDate(String(value));
    }
    return String(value);
  }, []);

  if (columns.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-adv-tertiary text-adv-body-s">
        No columns selected
      </div>
    );
  }

  const effectiveDimensions = processedData.dimensions;
  const hasDimensions = effectiveDimensions.length > 0;

  return (
    <div className="h-full overflow-auto">
      <table className="adv-table adv-table--compact">
        <thead className="bg-adv-sunken sticky top-0">
          <tr>
            {/* Dimension columns */}
            {effectiveDimensions.map((dim) => (
              <th
                key={dim}
                className="px-3 py-2 text-left"
              >
                {formatColumnName(dim)}
              </th>
            ))}
            {/* Metric columns */}
            {columns.map((col) => (
              <th
                key={col}
                className="px-3 py-2 text-right"
              >
                {getColumnDisplayName(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {processedData.rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-adv-sunken">
              {/* Dimension values */}
              {effectiveDimensions.map((dim) => (
                <td key={dim} className="px-3 py-2">
                  {formatDimensionValue(row[dim], dim)}
                </td>
              ))}
              {/* Metric values */}
              {columns.map((col) => (
                <td key={col} className="px-3 py-2 text-right adv-num whitespace-nowrap">
                  {formatValue(row[col] as number, col)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {showTotals && processedData.rows.length > 1 && (
          <tfoot>
            <tr>
              {/* Totals row: span dimension columns or show "Total" */}
              {hasDimensions && (
                effectiveDimensions.length === 1 ? (
                  <td className="px-3 py-2 font-bold">Total</td>
                ) : (
                  <>
                    <td className="px-3 py-2 font-bold">Total</td>
                    {effectiveDimensions.slice(1).map((dim) => (
                      <td key={dim} className="px-3 py-2 text-adv-tertiary">-</td>
                    ))}
                  </>
                )
              )}
              {/* Metric totals */}
              {columns.map((col) => (
                <td key={col} className="px-3 py-2 text-right adv-num font-bold whitespace-nowrap">
                  {formatValue(processedData.totals[col], col)}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function aggregate(values: number[], type: AggregationType): number {
  if (values.length === 0) return 0;

  switch (type) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'avg':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    case 'last':
      return values[values.length - 1];
    default:
      return values.reduce((a, b) => a + b, 0);
  }
}

function formatColumnName(col: string): string {
  return col
    .replace(/^campaign/, '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
}
